import { Component } from '@theme/component';

/**
 * Wraps a <video-carousel-track> of video cards and ensures only the active
 * (centered) card's video plays. Listens for the track's own
 * `slideshow:select` event rather than tracking scroll/index state itself,
 * so infinite-loop reordering and swipe keep working unmodified — this
 * component only reacts to "which slide is current now", it never drives
 * carousel navigation.
 *
 * @extends {Component}
 */
export class VideoCarouselComponent extends Component {
  /** @type {boolean} */
  #muted = true;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('slideshow:select', this.#handleSelect);
    this.#activateInitialCard();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('slideshow:select', this.#handleSelect);
    for (const card of this.#cards()) this.#pauseCard(card);
  }

  /** @returns {HTMLElement[]} real cards only — excludes the track's clone copies. */
  #cards() {
    return Array.from(this.querySelectorAll('[data-video-card]:not([data-clone])'));
  }

  #activateInitialCard() {
    const track = this.querySelector('video-carousel-track');
    const initialIndex = Number(track?.getAttribute('initial-slide') ?? 0) || 0;
    const cards = this.#cards();
    const initialCard = cards[initialIndex] ?? cards[0];

    if (initialCard) this.#activate(initialCard);
  }

  /** @param {Event} event */
  #handleSelect = (event) => {
    const slide = /** @type {CustomEvent} */ (event).detail?.slide;
    if (slide instanceof HTMLElement) this.#activate(slide);
  };

  /** @param {HTMLElement} activeCard */
  #activate(activeCard) {
    const index = activeCard.getAttribute('data-source-index');
    console.log(`[video-carousel] #activate — activating card index=${index}`, activeCard);

    for (const card of this.#cards()) {
      if (card === activeCard) {
        card.setAttribute('data-active', '');
        this.#playCard(card);
      } else {
        card.removeAttribute('data-active');
        this.#pauseCard(card);
      }
    }
  }

  /** @param {HTMLElement} card */
  #playCard(card) {
    const index = card.getAttribute('data-source-index');
    const video = card.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) {
      console.log(`[video-carousel] #playCard index=${index} — no <video> element found on this card`);
      return;
    }

    video.muted = this.#muted;
    video.loop = this.hasAttribute('loop');
    video.preload = 'auto';

    console.log(`[video-carousel] #playCard index=${index}`, {
      currentSrc: video.currentSrc,
      readyState: video.readyState,
      networkState: video.networkState,
      error: video.error,
      paused: video.paused,
    });

    if (!this.hasAttribute('autoplay')) return;

    video.addEventListener(
      'playing',
      () => {
        console.log(`[video-carousel] index=${index} fired "playing" event`);
        card.setAttribute('data-video-playing', '');
      },
      { once: true }
    );

    video.play().then(
      () => console.log(`[video-carousel] index=${index} play() resolved`),
      (error) => {
        // Autoplay was blocked (e.g. no prior user gesture). Leave the poster
        // and play button visible — .video-carousel-card__play stays clickable.
        console.error(`[video-carousel] index=${index} play() REJECTED:`, error.name, error.message, {
          currentSrc: video.currentSrc,
          readyState: video.readyState,
          networkState: video.networkState,
          videoError: video.error,
        });
        card.removeAttribute('data-video-playing');
      }
    );
  }

  /** @param {HTMLElement} card */
  #pauseCard(card) {
    const video = card.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;

    const index = card.getAttribute('data-source-index');
    console.log(`[video-carousel] #pauseCard index=${index}`);

    video.pause();
    video.preload = 'metadata';
    card.removeAttribute('data-video-playing');
  }

  /**
   * Toggles play/pause on the active card's video. Wired to both the media
   * area (click the video to pause/play) and the dedicated play button shown
   * when autoplay is off — that button is hidden once playing, so from then
   * on clicking the video itself is the only way to pause/resume it.
   * @param {Event} event
   */
  togglePlay(event) {
    const card = /** @type {HTMLElement} */ (event.target).closest('[data-video-card]');
    if (!(card instanceof HTMLElement) || !card.hasAttribute('data-active')) return;

    // A mouse-drag-to-scroll that ends back over the active card fires a
    // click too — don't let that also toggle playback.
    const scroller = card.closest('.video-carousel-track__scroller');
    if (scroller instanceof HTMLElement && scroller.dataset.justDragged === 'true') {
      delete scroller.dataset.justDragged;
      return;
    }

    const video = card.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;

    if (video.paused) {
      video.muted = this.#muted;
      video.addEventListener('playing', () => card.setAttribute('data-video-playing', ''), { once: true });
      video.play().catch(() => {});
    } else {
      video.pause();
      card.removeAttribute('data-video-playing');
    }
  }

  /** @param {Event} event */
  toggleMute(event) {
    const card = /** @type {HTMLElement} */ (event.target).closest('[data-video-card]');
    if (!(card instanceof HTMLElement)) return;

    this.#muted = !this.#muted;

    const video = card.querySelector('video');
    if (video instanceof HTMLVideoElement) video.muted = this.#muted;

    card.classList.toggle('video-carousel-card--muted', this.#muted);
  }
}

if (!customElements.get('video-carousel-component')) {
  customElements.define('video-carousel-component', VideoCarouselComponent);
}
